import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addToWaitlist } from '../../services/waitlistService';
import Navigation from '../Home/Navigation';
import Footer from '../Footer';
import styles from './Affiliate.module.css';

const Affiliate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  const handleGetReferralLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setIsDetectingLocation(true);
    setError(null);
    
    try {
      const success = await addToWaitlist(email);
      
      if (success) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        throw new Error('Failed to join affiliate program');
      }
    } catch (error) {
      console.error('Affiliate signup error:', error);
      setError('Failed to join affiliate program. Please try again.');
    } finally {
      setIsLoading(false);
      setIsDetectingLocation(false);
    }
  };

  return (
    <div className={styles.container}>
      <Navigation />
      
      <section className={styles.referralSection}>
        <div className={styles.referralContainer}>
          <div className={styles.referralHeader}>
            <div className={styles.referralIcon}>🎁</div>
            <h1 className={styles.referralTitle}>{t('referral.title')}</h1>
            <p className={styles.referralSubtitle}>
              {t('referral.subtitle')}
            </p>
          </div>

          <div className={styles.benefitsGrid}>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>🎉</div>
              <h3>{t('referral.benefits.you.label')}</h3>
              <div className={styles.benefitReward}>{t('referral.benefits.you.reward')}</div>
              <p>{t('referral.benefits.you.description')}</p>
            </div>

            <div className={styles.benefitArrow}>→</div>

            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>💝</div>
              <h3>{t('referral.benefits.they.label')}</h3>
              <div className={styles.benefitReward}>{t('referral.benefits.they.reward')}</div>
              <p>{t('referral.benefits.they.description')}</p>
            </div>

            <div className={styles.benefitArrow}>→</div>

            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>🚀</div>
              <h3>{t('referral.benefits.everyone.label')}</h3>
              <div className={styles.benefitReward}>{t('referral.benefits.everyone.reward')}</div>
              <p>{t('referral.benefits.everyone.description')}</p>
            </div>
          </div>

          <div className={styles.referralForm}>
            {!showSuccess ? (
              <form onSubmit={handleGetReferralLink}>
                <div className={styles.formGroup}>
                  <input
                    type="email"
                    className={styles.emailInput}
                    placeholder={t('referral.form.placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <button 
                    type="submit" 
                    className={styles.referralButton}
                    disabled={isLoading}
                  >
                    {isDetectingLocation 
                      ? 'Detecting location...' 
                      : isLoading 
                        ? 'Joining...' 
                        : t('referral.form.button')
                    }
                  </button>
                </div>
                {error && (
                  <div className={styles.errorMessage}>
                    {error}
                  </div>
                )}
              </form>
            ) : (
              <div className={styles.successMessage}>
                {t('referral.form.successMessage')}
              </div>
            )}
          </div>

          <div className={styles.howItWorks}>
            <h3 className={styles.howItWorksTitle}>{t('referral.howItWorks.title')}</h3>
            <div className={styles.stepsGrid}>
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <h4>{t('referral.howItWorks.step1.title')}</h4>
                <p>{t('referral.howItWorks.step1.description')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <h4>{t('referral.howItWorks.step2.title')}</h4>
                <p>{t('referral.howItWorks.step2.description')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>3</div>
                <h4>{t('referral.howItWorks.step3.title')}</h4>
                <p>{t('referral.howItWorks.step3.description')}</p>
              </div>
            </div>
          </div>

          <div className={styles.referralStats}>
            <div className={styles.stat}>
              <div className={styles.statNumber}>{t('referral.stats.commission.number')}</div>
              <div className={styles.statLabel}>{t('referral.stats.commission.label')}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statNumber}>{t('referral.stats.potential.number')}</div>
              <div className={styles.statLabel}>{t('referral.stats.potential.label')}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statNumber}>{t('referral.stats.tracking.number')}</div>
              <div className={styles.statLabel}>{t('referral.stats.tracking.label')}</div>
            </div>
          </div>

          <div className={styles.affiliateCta}>
            <p className={styles.affiliateText}>
              {t('referral.affiliate.text')}
            </p>
            <button 
              className={styles.affiliateButton}
              onClick={() => navigate('/pricing')}
            >
              View Pricing Plans
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Affiliate;
